import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { X, HelpCircle, Settings as SettingsIcon, Mail, BookOpen, Zap, ChevronDown, ChevronRight } from 'lucide-react';

const HelpModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('quick'); // 'quick', 'faq', 'docs'
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleNavigation = (path) => {
    onClose();
    navigate(path);
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@logly.work?subject=Logly Support Request';
  };

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I start tracking time?',
          a: 'You can start tracking time in three ways: 1) Use the Voice Entry button to speak your time entry, 2) Click Manual Entry to type details, or 3) Use Quick Start Timer cards to begin tracking for a specific project.'
        },
        {
          q: 'What is the 7-day trial period?',
          a: 'New users get a 7-day free trial with full access to all features. After the trial, you can subscribe to continue using Logly.'
        }
      ]
    },
    {
      category: 'Project Management (Phase 1-3)',
      questions: [
        {
          q: 'How do I set a project budget?',
          a: 'Go to Projects page, create or edit a project, and set time budgets (hours) and/or money budgets. You can choose between fixed or flexible budget types. The system will automatically track spent amounts and alert you at 75%, 90%, and 100% thresholds.'
        },
        {
          q: 'How do hourly rates work?',
          a: 'Each project can have a custom hourly rate with multi-currency support (USD, EUR, GBP, JPY, AUD, CAD, CHF, CNY, INR, SGD). The system automatically calculates earnings based on logged hours. You can also mark rates as billable or non-billable.'
        },
        {
          q: 'What are budget alerts?',
          a: 'Budget alerts notify you when you reach certain thresholds: INFO (75%), WARNING (90%), and DANGER (100%+). They appear on project cards and the dashboard to help you stay within budget.'
        },
        {
          q: 'How do project timelines and deadlines work?',
          a: 'Set start dates and due dates for each project. The system calculates days until due and provides color-coded alerts: Red (overdue/critical), Orange (due in 0-3 days), Yellow (due in 4-7 days), Green (on track).'
        },
        {
          q: 'What can I do with archived projects?',
          a: 'Archive completed projects to keep your active list clean. Archived projects don\'t show in main views but retain all data. You can restore them anytime from the Archive tab in Projects page.'
        },
        {
          q: 'How do tags and categories help?',
          a: 'Tags let you label projects with multiple keywords (e.g., "client", "urgent", "research"). Categories organize projects into groups (e.g., "Client Work", "Internal", "Personal"). Both help with filtering and reporting.'
        },
        {
          q: 'What is a default project?',
          a: 'Set one project as default for quick logging. When you start a timer or voice entry without specifying a project, it automatically logs to your default project.'
        }
      ]
    },
    {
      category: 'Time Tracking Features',
      questions: [
        {
          q: 'How does the Desktop Timer work?',
          a: 'The Desktop Timer lets you track time in real-time. Start a timer, and it runs in the background showing elapsed time. You can pause, resume, or stop it. When stopped, add a description before saving the entry.'
        },
        {
          q: 'Can I edit time entries after creating them?',
          a: 'Yes! Click any time entry card to edit the date, time, duration, project, description, or entry type. Changes are saved immediately.'
        },
        {
          q: 'What is the AI Project Predictor?',
          a: 'The AI Project Predictor learns your time tracking patterns and automatically suggests the correct project when you create entries. It learns from time-of-day, day-of-week, keywords, and attendees.'
        }
      ]
    },
    {
      category: 'Voice Notes',
      questions: [
        {
          q: 'How do I create a voice note?',
          a: 'Navigate to Notes tab, click "New Note", select meeting date, optionally add time and project, then click "Tap to Start Recording". Speak for up to 5 minutes, then click "Stop Recording". AI will transcribe and summarize your note automatically.'
        },
        {
          q: 'How does AI summarization work?',
          a: 'After recording, AI uses GPT-3.5 to analyze your transcript and generate 3-5 bullet points highlighting key decisions, action items, and important information. The summary focuses on what matters most.'
        },
        {
          q: 'Can I link notes to time entries?',
          a: 'Yes! When creating or editing a note, you can create a time entry linked to it. This allows you to track meeting time while keeping detailed notes. You can also link existing notes to time entries using the "Link" button.'
        },
        {
          q: 'How do I export notes?',
          a: 'Click the download icon on any note card, or open note details and click "Export as Text" to download as a text file. You can also click "Copy" to copy the note to clipboard.'
        },
        {
          q: 'Can I search notes?',
          a: 'Yes! Use the search bar to find notes by content. You can also filter notes by project, date range, or time entry status. Notes are searchable by transcript and summary content.'
        },
        {
          q: 'What happens if I discard a note?',
          a: 'Discarding a note permanently deletes it from your account. The note is removed immediately from your list. Make sure you really want to discard it before confirming.'
        }
      ]
    },
    {
      category: 'Calendar & To-Do List',
      questions: [
        {
          q: 'How does the calendar view work?',
          a: 'The calendar shows all your time entries by date and hour. Click any time slot to create a new entry. Entries dynamically expand based on duration and show project colors. You can edit or delete entries directly from the calendar.'
        },
        {
          q: 'What is the Weekly To-Do list?',
          a: 'The To-Do panel lets you plan tasks across 5 weeks. Add tasks with titles, descriptions, due dates, priority levels, and repeat settings. Tasks can be marked as complete and appear in your dashboard summary.'
        }
      ]
    },
    {
      category: 'Dashboard & Reports',
      questions: [
        {
          q: 'What does the Enhanced Dashboard show?',
          a: 'The dashboard displays: 1) Key metrics (hours worked, earnings, active projects), 2) Week-over-week trends, 3) Project cards with budget progress and alerts, 4) Weekly insights (most productive project, needs attention), 5) Upcoming to-dos with due dates.'
        },
        {
          q: 'How do I export my data?',
          a: 'Go to Settings and use the Export Data feature to download your time entries as CSV or PDF. Choose a date range and select which projects to include.'
        }
      ]
    },
    {
      category: 'Account & Settings',
      questions: [
        {
          q: 'How do I reset my password?',
          a: 'Click "Forgot Password?" on the login screen or use the "Reset Password via Email" button in Settings. You\'ll receive an email with a reset link valid for 1 hour.'
        },
        {
          q: 'Can I change my subscription?',
          a: 'Yes! Active subscribers can manage their subscription via the Stripe Customer Portal accessible from Settings. You can update payment methods, cancel, or view invoices.'
        }
      ]
    }
  ];

  const documentation = [
    {
      title: 'Voice Notes',
      content: [
        'Record voice notes with AI transcription and summarization',
        'Navigate to Notes tab → Click "New Note" → Start recording (max 5 minutes)',
        'AI automatically transcribes speech and generates summary',
        'Link notes to time entries for time tracking',
        'Export notes as text files or copy to clipboard',
        'Search notes by content, filter by project or date range'
      ]
    },
    {
      title: 'Voice Commands',
      content: [
        '"Log 2 hours on [project name]" - Quick time entry',
        '"Start timer for [project]" - Begin live tracking',
        '"Stop recording" - End voice input',
        'Speak naturally - AI processes your intent'
      ]
    },
    {
      title: 'Keyboard Shortcuts',
      content: [
        'Space - Start/Stop current timer',
        'N - New manual entry',
        'S - Go to Settings',
        'Esc - Close any dialog'
      ]
    },
    {
      title: 'Project Budget Types',
      content: [
        'Fixed Budget - Hard limit, stops at 100%',
        'Flexible Budget - Allows going over, tracks overage',
        'Time Budget - Track hours against limit',
        'Money Budget - Track cost against limit',
        'Both - Track time AND money simultaneously'
      ]
    },
    {
      title: 'Currency Support',
      content: [
        'Supported: USD, EUR, GBP, JPY, AUD, CAD, CHF, CNY, INR, SGD',
        'Auto-conversion between currencies',
        'Live exchange rates',
        'Multi-currency reporting'
      ]
    },
    {
      title: 'Entry Types',
      content: [
        'Work - Standard work time',
        'Meeting - Time spent in meetings',
        'Break - Scheduled breaks',
        'Other - Miscellaneous activities'
      ]
    },
    {
      title: 'Priority Levels',
      content: [
        'High - Urgent tasks, show red indicator',
        'Normal - Standard priority, blue indicator',
        'Low - Can be deferred, gray indicator'
      ]
    },
    {
      title: 'Repeat Settings (To-Dos)',
      content: [
        'None - One-time task',
        'Daily - Repeats every day',
        'Weekly - Repeats every week',
        'Monthly - Repeats every month'
      ]
    },
    {
      title: 'Status Indicators',
      content: [
        '✓ On Track (Green) - Project is healthy',
        '⚠️ Watch (Yellow) - 75-89% budget used',
        '🚨 At Risk (Orange) - 90-99% budget used',
        '🔴 Critical (Red) - 100%+ budget or overdue'
      ]
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] md:max-w-[700px] max-h-[85vh] md:max-h-[90vh] overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors">
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-700 dark:to-blue-700 text-white p-6 relative transition-colors">
            <div className="flex items-center gap-3">
              <HelpCircle className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Help & Support</h2>
            </div>
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              data-testid="close-help-modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 transition-colors">
            <div className="flex">
              <button
                onClick={() => setActiveSection('quick')}
                className={`flex-1 py-3 px-4 font-medium transition-colors ${
                  activeSection === 'quick'
                    ? 'text-cyan-600 dark:text-cyan-400 border-b-2 border-cyan-600 dark:border-cyan-400 bg-white dark:bg-gray-800'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                Quick Actions
              </button>
              <button
                onClick={() => setActiveSection('faq')}
                className={`flex-1 py-3 px-4 font-medium transition-colors ${
                  activeSection === 'faq'
                    ? 'text-cyan-600 dark:text-cyan-400 border-b-2 border-cyan-600 dark:border-cyan-400 bg-white dark:bg-gray-800'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                FAQs
              </button>
              <button
                onClick={() => setActiveSection('docs')}
                className={`flex-1 py-3 px-4 font-medium transition-colors ${
                  activeSection === 'docs'
                    ? 'text-cyan-600 dark:text-cyan-400 border-b-2 border-cyan-600 dark:border-cyan-400 bg-white dark:bg-gray-800'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                Quick Start
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto max-h-[calc(90vh-220px)] p-6 bg-white dark:bg-gray-800 transition-colors">
            
            {/* Quick Actions Section */}
            {activeSection === 'quick' && (
              <div className="space-y-6">
                <section>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200 transition-colors">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleNavigation('/settings')}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-left border border-gray-200 dark:border-gray-600"
                    >
                      <SettingsIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors">Settings</span>
                    </button>
                    <button
                      onClick={() => setActiveSection('faq')}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-left border border-gray-200 dark:border-gray-600"
                    >
                      <span className="text-2xl">❓</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors">FAQs</span>
                    </button>
                    <button
                      onClick={() => handleNavigation('/docs')}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-left border border-gray-200 dark:border-gray-600"
                    >
                      <BookOpen className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors">Full Documentation</span>
                    </button>
                    <button
                      onClick={handleContactSupport}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-left border border-gray-200 dark:border-gray-600"
                    >
                      <Mail className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors">Contact Support</span>
                    </button>
                  </div>
                </section>

                {/* Keyboard Shortcuts */}
                <section>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200 transition-colors">Keyboard Shortcuts</h3>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2 border border-gray-200 dark:border-gray-600 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 transition-colors">Start/Stop current timer</span>
                      <kbd className="px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded border-2 border-gray-300 dark:border-gray-600 text-sm font-mono transition-colors">Space</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 transition-colors">New manual entry</span>
                      <kbd className="px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded border-2 border-gray-300 dark:border-gray-600 text-sm font-mono transition-colors">N</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 transition-colors">Go to Settings</span>
                      <kbd className="px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded border-2 border-gray-300 dark:border-gray-600 text-sm font-mono transition-colors">S</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300 transition-colors">Close dialogs</span>
                      <kbd className="px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded border-2 border-gray-300 dark:border-gray-600 text-sm font-mono transition-colors">Esc</kbd>
                    </div>
                  </div>
                </section>

                {/* Pro Tips */}
                <section>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200 flex items-center gap-2 transition-colors">
                    <Zap className="w-5 h-5 text-yellow-500 dark:text-yellow-400 transition-colors" />
                    Pro Tips
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                      <span className="text-xl">💡</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Record voice notes with AI transcription and summarization for meeting documentation</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                      <span className="text-xl">💡</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Link notes to time entries to track meeting time while keeping detailed notes</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                      <span className="text-xl">💡</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Set project budgets and hourly rates to track profitability and stay within limits</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                      <span className="text-xl">💡</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Use tags and categories to organize projects for better filtering and reporting</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                      <span className="text-xl">💡</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Set deadlines for projects to get automatic alerts when approaching due dates</p>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                      <span className="text-xl">💡</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">Archive completed projects to keep your workspace clean while preserving all data</p>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* FAQs Section */}
            {activeSection === 'faq' && (
              <div className="space-y-4">
                {faqs.map((category, categoryIndex) => (
                  <div key={categoryIndex}>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2 transition-colors">
                      <span className="w-2 h-2 rounded-full bg-cyan-500 dark:bg-cyan-400 transition-colors"></span>
                      {category.category}
                    </h3>
                    <div className="space-y-2">
                      {category.questions.map((faq, faqIndex) => {
                        const key = `${categoryIndex}-${faqIndex}`;
                        const isExpanded = expandedFaq === key;
                        return (
                          <div key={key} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden transition-colors">
                            <button
                              onClick={() => setExpandedFaq(isExpanded ? null : key)}
                              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors bg-white dark:bg-gray-800"
                            >
                              <span className="font-medium text-gray-800 dark:text-gray-200 pr-4 transition-colors">{faq.q}</span>
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0 transition-colors" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-colors" />
                              )}
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 transition-colors">
                                {faq.a}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Documentation Section */}
            {activeSection === 'docs' && (
              <div className="space-y-6">
                {/* Interactive Demo */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2 transition-colors">
                    <Zap className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                    Interactive Demo: Log Time with Voice Entry
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors">
                    {/*ARCADE EMBED START*/}
                    <div style={{ position: 'relative', paddingBottom: 'calc(47.4653% + 41px)', height: 0, width: '100%' }}>
                      <iframe 
                        src="https://demo.arcade.software/bXWJ7hd8eIikKX5u3ZC7?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true" 
                        title="Log Time with Voice Entry for a Project" 
                        frameBorder="0" 
                        loading="lazy" 
                        allow="clipboard-write"
                        style={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          width: '100%', 
                          height: '100%',
                          colorScheme: 'light'
                        }}
                        className="rounded-lg"
                      ></iframe>
                    </div>
                    {/*ARCADE EMBED END*/}
                  </div>
                </section>

                {documentation.map((doc, index) => (
                  <section key={index}>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2 transition-colors">
                      <BookOpen className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                      {doc.title}
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors">
                      <ul className="space-y-2">
                        {doc.content.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                            <span className="text-cyan-600 dark:text-cyan-400 mt-1 transition-colors">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                ))}
              </div>
            )}

          </div>

          {/* Footer Button */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors">
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-700 dark:to-blue-700 text-white font-semibold rounded-lg hover:from-cyan-700 hover:to-blue-700 dark:hover:from-cyan-800 dark:hover:to-blue-800 transition-all min-h-[44px]"
            >
              Got it, thanks!
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default HelpModal;
